import { useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import ReactMarkdown, { type Components, type UrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/utils';

interface MarkdownProps {
  /** Markdown source. Rendered to React elements — raw HTML is NOT executed
   *  (react-markdown ignores it by default), so there's no XSS surface. */
  children: string;
  className?: string;
  /**
   * When true, block (fenced) code renders with soft-wrapping (no horizontal
   * scroll) and a copy button. Use for copy-paste prose like example prompts;
   * leave off for real code, which should scroll rather than wrap.
   */
  copyableCode?: boolean;
  /** Keep single line breaks, as people type them in a plain textarea (Markdown
   *  would join the lines into one paragraph). Code blocks are left alone. */
  keepLineBreaks?: boolean;
  /** Extra element overrides (merged over the copyable-code ones). */
  components?: Components;
  /** Replaces react-markdown's URL sanitizer — keep its default for anything
   *  you don't explicitly allow. */
  urlTransform?: UrlTransform;
}

/**
 * People often type "– ", "— " or "• " as bullets (e.g. pasted job specs).
 * Those aren't Markdown list markers, so the lines collapse into one paragraph.
 * Normalise a leading bullet glyph to a real Markdown "- " so it renders as a
 * proper list. Only the line-leading glyph is touched (en-dashes mid-sentence,
 * like "1–3 years", are left alone).
 */
function normalizeBullets(src: string): string {
  return src.replace(/^([ \t]*)[–—•]\s+/gm, '$1- ');
}

/** Turn each single newline outside fenced code into a Markdown hard break. */
export function hardenLineBreaks(src: string): string {
  const lines = src.split('\n');
  // The open fence (e.g. "````" or "~~~"); only the same character, at least as
  // long, closes it — so a ``` inside a ~~~ block doesn't end it (CommonMark).
  let fence: string | null = null;
  return lines
    .map((line, i) => {
      const marker = /^\s*(`{3,}|~{3,})/.exec(line)?.[1];
      if (marker && (fence === null || (marker[0] === fence[0] && marker.length >= fence.length))) {
        fence = fence === null ? marker : null;
        return line;
      }
      const inFence = fence !== null;
      const next = lines[i + 1];
      const breaks = !inFence && line.trim() !== '' && next !== undefined && next.trim() !== '';
      return breaks ? `${line.replace(/\s+$/, '')}  ` : line;
    })
    .join('\n');
}

/** A fenced code block that soft-wraps and carries a copy-to-clipboard button. */
function CopyablePre({ children, className, ...props }: React.ComponentPropsWithoutRef<'pre'>) {
  const ref = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const text = ref.current?.textContent?.replace(/\n$/, '') ?? '';
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable (e.g. insecure context) — no-op
    }
  };

  return (
    <div className="group relative">
      <pre
        ref={ref}
        className={cn('whitespace-pre-wrap break-words pr-10', className)}
        {...props}
      >
        {children}
      </pre>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? 'Copied' : 'Copy to clipboard'}
        className="text-muted-foreground hover:bg-muted hover:text-foreground absolute top-2 right-2 rounded-md p-1.5 opacity-70 transition group-hover:opacity-100"
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  );
}

const COPYABLE_COMPONENTS: Components = { pre: CopyablePre };

/**
 * Renders stored Markdown as safe, Tailwind-typography-styled content.
 * Use anywhere a Markdown-backed field (e.g. a description) is displayed.
 */
export function Markdown({
  children,
  className,
  copyableCode = false,
  keepLineBreaks = false,
  components,
  urlTransform,
}: MarkdownProps) {
  if (!children?.trim()) return null;
  const source = normalizeBullets(children);
  return (
    <div
      className={cn(
        'prose prose-sm dark:prose-invert max-w-none',
        // Keep it compact + on-brand: links use the primary color, no heavy margins.
        'prose-a:text-primary prose-headings:font-semibold',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={
          copyableCode || components
            ? { ...(copyableCode ? COPYABLE_COMPONENTS : {}), ...components }
            : undefined
        }
        urlTransform={urlTransform}
      >
        {keepLineBreaks ? hardenLineBreaks(source) : source}
      </ReactMarkdown>
    </div>
  );
}
